import { useState, useEffect, useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, Loader2, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Badge } from '@/components/ui/badge'
import { Prescription } from '@/types/prescription'
import { PrescriptionGroup } from '@/types/prescriptionGroup'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog.jsx"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import React from 'react'

// Interface to represent user with their prescriptions
interface UserWithPrescriptions {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  prescriptionGroups: PrescriptionGroup[];
}

// Interface for prescriptions needing review
interface PrescriptionNeedsReview {
  id: number;
  product_id: string;
  product_name: string;
  prescription_group_id: number;
  missing_data_notes: string;
  repeats: number;
  prescription_expiry_date: string;
  created_at: string;
  shopify_order_id: string;
  email: string;
  first_name: string;
  last_name: string;
}

// PrescriptionDataEditor component
function PrescriptionDataEditor() {
  const { token } = useAuth();
  const [prescriptions, setPrescriptions] = useState<PrescriptionNeedsReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPrescription, setSelectedPrescription] = useState<PrescriptionNeedsReview | null>(null);
  const [repeats, setRepeats] = useState<number>(0);
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch prescriptions that need review
  useEffect(() => {
    const fetchPrescriptionsNeedingReview = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/admin/prescriptions/needs-review', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch prescriptions that need review');
        }
        
        const data = await response.json();
        setPrescriptions(data.prescriptions);
      } catch (error) {
        console.error('Error fetching prescriptions needing review:', error);
        setError('Failed to load prescriptions that need review');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (token) {
      fetchPrescriptionsNeedingReview();
    }
  }, [token]);
  
  // Open dialog to edit prescription data
  const handleEdit = (prescription: PrescriptionNeedsReview) => {
    setSelectedPrescription(prescription);
    
    // Set initial values
    setRepeats(prescription.repeats || 0);
    
    // Set a default expiry date (1 year from now) if not already set
    if (prescription.prescription_expiry_date) {
      setExpiryDate(prescription.prescription_expiry_date.split('T')[0]);
    } else {
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      setExpiryDate(oneYearFromNow.toISOString().split('T')[0]);
    }
  };
  
  // Close dialog and reset form
  const handleCloseDialog = () => {
    setSelectedPrescription(null);
    setRepeats(0);
    setExpiryDate('');
  };
  
  // Submit updated prescription data
  const handleSubmit = async () => {
    if (!selectedPrescription) return;
    
    try {
      setIsSubmitting(true);
      
      const response = await fetch(`/api/admin/prescriptions/${selectedPrescription.id}/complete-data`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          repeats,
          prescription_expiry_date: expiryDate
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update prescription data');
      }
      
      // Remove the updated prescription from the list
      setPrescriptions(prev => prev.filter(p => p.id !== selectedPrescription.id));
      
      toast('Prescription data updated successfully', {
        description: `Updated prescription data for ${selectedPrescription.product_name}`
      });
      
      // Close the dialog
      handleCloseDialog();
      
    } catch (error) {
      console.error('Error updating prescription data:', error);
      toast('Failed to update prescription data', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  return (
    <div className="mt-8">
      <Card className="shadow-sm border border-gray-200">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              <CardTitle className="text-xl font-semibold text-gray-800">Prescriptions Needing Review</CardTitle>
            </div>
            <Badge variant="outline" className="bg-amber-100 text-amber-800">
              {prescriptions.length} Pending
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            These prescriptions are missing prescription data (repeats and expiry date) and need to be reviewed.
          </p>
        </CardHeader>
        
        <CardContent className="p-0">
          {error ? (
            <div className="text-red-500 p-4 m-4 rounded bg-red-50">{error}</div>
          ) : isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-500" />
              <span>Loading prescriptions...</span>
            </div>
          ) : prescriptions.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              No prescriptions need review at this time
            </div>
          ) : (
            <div className="border-b">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="font-medium text-gray-600">Product</TableHead>
                    <TableHead className="font-medium text-gray-600">Customer</TableHead>
                    <TableHead className="font-medium text-gray-600">Order ID</TableHead>
                    <TableHead className="font-medium text-gray-600">Date</TableHead>
                    <TableHead className="font-medium text-gray-600">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                
                <TableBody>
                  {prescriptions.map(prescription => (
                    <TableRow key={prescription.id}>
                      <TableCell className="font-medium">{prescription.product_name}</TableCell>
                      <TableCell>
                        <div>
                          <div>{prescription.first_name} {prescription.last_name}</div>
                          <div className="text-sm text-gray-500">{prescription.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>{prescription.shopify_order_id}</TableCell>
                      <TableCell>{formatDate(prescription.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          onClick={() => handleEdit(prescription)}
                        >
                          Update Data
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Edit Prescription Data Dialog */}
      <Dialog 
        open={!!selectedPrescription} 
        onOpenChange={(open) => !open && handleCloseDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Prescription Data</DialogTitle>
            <DialogDescription>
              Add the missing prescription data for this product.
            </DialogDescription>
          </DialogHeader>
          
          {selectedPrescription && (
            <div className="space-y-4 my-4">
              <div className="border-b pb-3">
                <h3 className="font-semibold text-lg">{selectedPrescription.product_name}</h3>
                <p className="text-sm text-gray-600">
                  Order #{selectedPrescription.shopify_order_id}
                </p>
                <p className="text-sm text-gray-600">
                  Customer: {selectedPrescription.first_name} {selectedPrescription.last_name} ({selectedPrescription.email})
                </p>
                {selectedPrescription.missing_data_notes && (
                  <div className="mt-2 p-2 bg-amber-50 text-amber-800 text-sm rounded">
                    <p className="font-medium">Notes:</p>
                    <p className="whitespace-pre-line">{selectedPrescription.missing_data_notes}</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Repeats
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="12"
                    value={repeats}
                    onChange={(e) => setRepeats(parseInt(e.target.value) || 0)}
                    className="w-full border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The number of times this prescription can be refilled
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prescription Expiry Date
                  </label>
                  <Input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    className="w-full border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The date when this prescription expires
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : 'Save Prescription Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PrescriptionManagement() {
  const { token } = useAuth()
  const [prescriptions, setPrescriptions] = useState<PrescriptionGroup[]>([])
  const [userPrescriptions, setUserPrescriptions] = useState<UserWithPrescriptions[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState('')
  const [itemToDelete, setItemToDelete] = useState<Prescription | null>(null)
  const [groupToDelete, setGroupToDelete] = useState<PrescriptionGroup | null>(null)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [expandedUsers, setExpandedUsers] = useState<number[]>([])
  const [reminderItem, setReminderItem] = useState<Prescription | null>(null)
  const [newReminderDate, setNewReminderDate] = useState<string>('')
  const [clearLastReminder, setClearLastReminder] = useState(true)

  useEffect(() => {
    const fetchPrescriptions = async () => {
      try {
        const response = await fetch('/api/prescriptions', {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        if (!response.ok) throw new Error('Failed to fetch prescriptions');

        const data = await response.json();
        console.log('Raw prescription data:', data);

        // Group prescriptions by prescription_group_id
        const groupedData = data.reduce((acc: { [key: string]: PrescriptionGroup }, item: any) => {
          const groupId = item.prescription_group_id;
          
          const userId = item.user?.id || item.user?.email;
          const userEmail = item.user?.email || `no-email-${groupId}`;
          const userFirstName = item.user?.first_name || 'Unknown';
          const userLastName = item.user?.last_name || 'User';
          
          if (!acc[groupId]) {
            acc[groupId] = {
              id: groupId,
              prescription_group_id: groupId,
              items: [],
              user: {
                id: userId,
                first_name: userFirstName,
                last_name: userLastName,
                email: userEmail
              }
            };
          }
          
          // Create a properly typed Prescription object
          const prescription: Prescription = {
            id: item.id,
            prescription_group_id: groupId,
            product_id: item.product_id || '',
            product_name: item.product_name,
            quantity: item.quantity,
            status: item.status,
            start_date: item.start_date,
            expiry_date: item.expiry_date,
            prescription_expiry_date: item.prescription_expiry_date,
            repeats: item.repeats,
            doctor_name: item.doctor_name || '',
            created_at: item.created_at || '',
            updated_at: item.updated_at || '',
            next_reminder_date: item.next_reminder_date,
            last_reminder_sent: item.last_reminder_sent,
            last_renewal_date: item.last_renewal_date,
            shopify_order_id: item.shopify_order_id || '',
            shopify_line_item_id: item.shopify_line_item_id || ''
          };
          
          acc[groupId].items.push(prescription);
          return acc;
        }, {});

        const prescriptionGroups: PrescriptionGroup[] = Object.values(groupedData);
        setPrescriptions(prescriptionGroups);
        
        // Group prescriptions by user email to ensure unique users
        const userMap = new Map<string, UserWithPrescriptions>();
        
        prescriptionGroups.forEach((group: PrescriptionGroup) => {
          if (!group.user) return;
          
          // Use email as the key for user grouping instead of ID
          const userEmail = group.user.email;
          
          if (!userMap.has(userEmail)) {
            userMap.set(userEmail, {
              id: group.user.id,
              first_name: group.user.first_name,
              last_name: group.user.last_name,
              email: userEmail,
              prescriptionGroups: []
            });
          }
          
          const userPrescription = userMap.get(userEmail);
          if (userPrescription) {
            userPrescription.prescriptionGroups.push(group);
          }
        });
        
        const groupedUsers = Array.from(userMap.values());
        setUserPrescriptions(groupedUsers);
        
        // Don't expand all users by default - leave them collapsed for cleaner UI
        setExpandedUsers([]);
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching prescriptions:', error);
        setError('Failed to load prescriptions');
        setIsLoading(false);
      }
    };

    if (token) {
      fetchPrescriptions();
    }
  }, [token]);

  const filteredUserPrescriptions = useMemo(() => {
    return userPrescriptions.filter(user =>
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.prescriptionGroups.some(group =>
        group.items.some(item =>
          item.product_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    );
  }, [userPrescriptions, searchTerm]);

  const sendTestRenewalEmail = async (prescriptionId: number) => {
    try {
      const response = await fetch(`/api/prescriptions/test-reminder/${prescriptionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to send test email');

      const data = await response.json();
      console.log('Test email response:', data);

      toast('Test email sent successfully', {
        description: 'A test renewal email has been sent'
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast('Failed to send test email', {
        description: 'There was an error sending the test email'
      });
    }
  }

  const handleDeleteItem = async (prescription: Prescription) => {
    try {
      const response = await fetch(`/api/prescriptions/${prescription.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) throw new Error('Failed to delete prescription');

      // Update both data structures
      setPrescriptions(prevPrescriptions => 
        prevPrescriptions.map(group => ({
          ...group,
          items: group.items.filter(item => item.id !== prescription.id)
        })).filter(group => group.items.length > 0)
      );
      
      setUserPrescriptions(prevUsers => {
        return prevUsers.map(user => ({
          ...user,
          prescriptionGroups: user.prescriptionGroups
            .map(group => ({
              ...group,
              items: group.items.filter(item => item.id !== prescription.id)
            }))
            .filter(group => group.items.length > 0)
        })).filter(user => user.prescriptionGroups.length > 0);
      });
      
      toast('Prescription item deleted successfully', {
        description: 'The prescription has been removed'
      });
    } catch (error) {
      console.error('Error deleting prescription:', error);
      toast('Failed to delete prescription item', {
        description: 'There was an error deleting the prescription'
      });
    } finally {
      setItemToDelete(null);
    }
  };

  const handleDeleteGroup = async (group: PrescriptionGroup) => {
    try {
      const response = await fetch(`/api/prescriptions/group/${group.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) throw new Error('Failed to delete prescription group');

      // Update both data structures
      setPrescriptions(prev => prev.filter(p => p.id !== group.id));
      
      setUserPrescriptions(prevUsers => {
        return prevUsers.map(user => ({
          ...user,
          prescriptionGroups: user.prescriptionGroups.filter(g => g.id !== group.id)
        })).filter(user => user.prescriptionGroups.length > 0);
      });
      
      toast('Prescription group deleted successfully', {
        description: 'The prescription group has been removed'
      });
    } catch (error) {
      console.error('Error deleting prescription group:', error);
      toast('Failed to delete prescription group', {
        description: 'There was an error deleting the prescription group'
      });
    } finally {
      setGroupToDelete(null);
    }
  };

  const handleSelectItem = (itemId: number) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = (group: PrescriptionGroup) => {
    const groupItemIds = group.items.map(item => item.id);
    setSelectedItems(prev => {
      const allSelected = groupItemIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !groupItemIds.includes(id));
      }
      return [...prev, ...groupItemIds];
    });
  };

  const handleSelectAllUserItems = (user: UserWithPrescriptions) => {
    const userItemIds = user.prescriptionGroups.flatMap(group => 
      group.items.map(item => item.id)
    );
    
    setSelectedItems(prev => {
      const allSelected = userItemIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !userItemIds.includes(id));
      }
      return [...prev, ...userItemIds];
    });
  };

  const handleDeleteSelected = async () => {
    try {
      // If all items in a group are selected, use group delete
      const groupsToDelete = prescriptions.filter(group => 
        group.items.every(item => selectedItems.includes(item.id))
      );

      // Individual items to delete (not part of fully selected groups)
      const itemsToDelete = selectedItems.filter(itemId => 
        !groupsToDelete.some(group => 
          group.items.some(item => item.id === itemId)
        )
      );

      // Delete groups first
      for (const group of groupsToDelete) {
        await handleDeleteGroup(group);
      }

      // Then delete individual items
      for (const itemId of itemsToDelete) {
        const item = prescriptions
          .flatMap(g => g.items)
          .find(i => i.id === itemId);
        if (item) {
          await handleDeleteItem(item);
        }
      }

      setSelectedItems([]);
    } catch (error) {
      console.error('Error in batch delete:', error);
      toast('Error deleting selected items', {
        description: 'There was an error deleting the selected items'
      });
    }
  };

  const toggleUserExpanded = (userId: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    setExpandedUsers(prev => {
      const newExpanded = prev.includes(userId) ? [] : [userId];
      console.log('Toggling user:', userId, 'Previous state:', prev, 'New state:', newExpanded);
      return newExpanded;
    });
  };
  
  // Handle the reminder date update
  const handleUpdateReminder = (prescription: Prescription) => {
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Format date as YYYY-MM-DD
    const formattedDate = tomorrow.toISOString().split('T')[0];
    
    setReminderItem(prescription);
    setNewReminderDate(formattedDate);
    setClearLastReminder(true);
  };
  
  // Save the updated reminder date
  const saveReminderUpdate = async () => {
    if (!reminderItem || !newReminderDate) return;
    
    try {
      const response = await fetch('/api/admin/update-prescription-reminder', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prescriptionId: reminderItem.id,
          reminderDate: newReminderDate,
          clearLastReminder: clearLastReminder
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update reminder date');
      }
      
      const data = await response.json();
      
      // Update local state
      setPrescriptions(prevGroups => {
        return prevGroups.map(group => {
          if (group.id === reminderItem.prescription_group_id) {
            return {
              ...group,
              items: group.items.map(item => {
                if (item.id === reminderItem.id) {
                  return {
                    ...item,
                    next_reminder_date: data.prescription.next_reminder_date,
                    last_reminder_sent: clearLastReminder ? null : item.last_reminder_sent
                  };
                }
                return item;
              })
            };
          }
          return group;
        });
      });
      
      // Also update in userPrescriptions
      setUserPrescriptions(prevUsers => {
        return prevUsers.map(user => ({
          ...user,
          prescriptionGroups: user.prescriptionGroups.map(group => {
            if (group.id === reminderItem.prescription_group_id) {
              return {
                ...group,
                items: group.items.map(item => {
                  if (item.id === reminderItem.id) {
                    return {
                      ...item,
                      next_reminder_date: data.prescription.next_reminder_date,
                      last_reminder_sent: clearLastReminder ? null : item.last_reminder_sent
                    };
                  }
                  return item;
                })
              };
            }
            return group;
          })
        }));
      });
      
      toast('Reminder date updated', {
        description: `Reminder date updated to ${new Date(newReminderDate).toLocaleDateString()}`
      });
      
      // Reset state
      setReminderItem(null);
      setNewReminderDate('');
      
    } catch (error) {
      console.error('Error updating reminder date:', error);
      toast('Failed to update reminder date', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  return (
    <div className="p-6">
      <Tabs defaultValue="all-prescriptions" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="all-prescriptions">All Prescriptions</TabsTrigger>
          <TabsTrigger value="needs-review">
            Needs Review
            <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">
              !
            </Badge>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="all-prescriptions">
          <Card className="shadow-sm border border-gray-200">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 pb-4 border-b">
              <CardTitle className="text-xl font-semibold text-gray-800">Prescriptions</CardTitle>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search prescriptions..."
                    className="pl-8 w-full sm:w-64 border-gray-300 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                {selectedItems.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="bg-red-500 hover:bg-red-600 text-white mt-2 sm:mt-0"
                    onClick={() => setGroupToDelete({ 
                      id: 0, 
                      prescription_group_id: 0,
                      items: [], 
                      user: { id: 0, first_name: '', last_name: '', email: '' } 
                    })}
                  >
                    Delete Selected ({selectedItems.length})
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {error ? (
                <div className="text-red-500 p-4 m-4 rounded bg-red-50">{error}</div>
              ) : isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-500" />
                  <span>Loading prescriptions...</span>
                </div>
              ) : userPrescriptions.length === 0 ? (
                <div className="text-center p-8 text-gray-500">
                  No prescriptions found
                </div>
              ) : (
                <div className="border-b">
                  <Table>
                    <TableHeader className="bg-gray-50">
                      <TableRow>
                        <TableHead className="w-12 pl-4"></TableHead>
                        <TableHead className="font-medium text-gray-600">Patient</TableHead>
                        <TableHead className="font-medium text-gray-600">Orders</TableHead>
                        <TableHead className="font-medium text-gray-600">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUserPrescriptions.map((user) => (
                        <React.Fragment key={user.id}>
                          <TableRow className="hover:bg-gray-50">
                            <TableCell className="pl-4">
                              <Checkbox
                                checked={user.prescriptionGroups.flatMap(g => g.items).every(item => 
                                  selectedItems.includes(item.id)
                                )}
                                onCheckedChange={() => handleSelectAllUserItems(user)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell 
                              className="cursor-pointer"
                              onClick={(e) => toggleUserExpanded(user.id, e)}
                            >
                              <div className="flex items-center">
                                {expandedUsers.includes(user.id) ? 
                                  <ChevronDown className="h-4 w-4 mr-2" /> : 
                                  <ChevronRight className="h-4 w-4 mr-2" />
                                }
                                <div>
                                  <div className="font-medium text-gray-800">{user.first_name} {user.last_name}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="inline-flex items-center justify-center rounded-full bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-700">
                                {user.prescriptionGroups.length} order{user.prescriptionGroups.length !== 1 ? 's' : ''}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-500 hover:bg-blue-100"
                                onClick={(e) => toggleUserExpanded(user.id, e)}
                              >
                                {expandedUsers.includes(user.id) ? 'Hide Orders' : 'Show Orders'}
                              </Button>
                            </TableCell>
                          </TableRow>
                          
                          {expandedUsers.includes(user.id) && user.prescriptionGroups.map((group) => (
                            <TableRow key={`group-${group.id}`} className="bg-gray-50 border-t border-b">
                              <TableCell colSpan={4} className="p-0">
                                <div className="p-4">
                                  <div className="bg-white rounded border border-gray-200 mb-4">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 border-b bg-gray-50">
                                      <div className="flex items-center space-x-2">
                                        <Checkbox
                                          checked={group.items.every(item => selectedItems.includes(item.id))}
                                          onCheckedChange={() => handleSelectAll(group)}
                                        />
                                        <div>
                                          <div className="font-medium">Order #{group.id}</div>
                                          <div className="text-xs text-gray-500">
                                            Invalid Date
                                          </div>
                                        </div>
                                      </div>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-2 sm:mt-0 w-full sm:w-auto"
                                        onClick={() => group.items.length > 0 && sendTestRenewalEmail(group.items[0].id)}
                                      >
                                        Send Test Email
                                      </Button>
                                    </div>
                                    
                                    <div className="divide-y">
                                      {group.items.map((item) => (
                                        <div key={item.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3">
                                          <div className="flex items-center space-x-3 mb-2 sm:mb-0">
                                            <Checkbox
                                              checked={selectedItems.includes(item.id)}
                                              onCheckedChange={() => handleSelectItem(item.id)}
                                            />
                                            <div>
                                              <div className="font-medium truncate max-w-[200px] sm:max-w-none">
                                                {item.product_name} 
                                                <span className="text-sm text-gray-500 ml-1">(Qty: {item.quantity})</span>
                                                {item.repeats !== undefined && item.repeats > 0 && (
                                                  <span className="text-sm text-blue-600 ml-1">
                                                    (Repeats: {item.repeats})
                                                  </span>
                                                )}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {item.next_reminder_date ? (
                                                  <>
                                                    Reminder: {new Date(item.next_reminder_date).toLocaleDateString()} 
                                                    {(() => {
                                                      const now = new Date();
                                                      const reminderDate = new Date(item.next_reminder_date);
                                                      const daysDiff = Math.round((reminderDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                                      
                                                      if (daysDiff < 0) {
                                                        return <span className="text-red-500 ml-1">(Overdue by {Math.abs(daysDiff)} days)</span>;
                                                      } else if (daysDiff === 0) {
                                                        return <span className="text-orange-500 ml-1">(Today)</span>;
                                                      } else if (daysDiff === 1) {
                                                        return <span className="text-orange-500 ml-1">(Tomorrow)</span>;
                                                      } else {
                                                        return <span className="ml-1">({daysDiff} days from now)</span>;
                                                      }
                                                    })()}
                                                  </>
                                                ) : 'No reminder scheduled'}
                                              </div>
                                              <div className="text-xs text-gray-500 flex flex-col sm:flex-row sm:gap-4">
                                                <span>
                                                  Expiry: {item.prescription_expiry_date ? 
                                                      new Date(item.prescription_expiry_date).toLocaleDateString() + ' (from prescription)' : 
                                                      new Date(item.expiry_date).toLocaleDateString() + ' (system default)'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex flex-wrap items-center space-x-0 sm:space-x-2 space-y-2 sm:space-y-0 w-full sm:w-auto">
                                            <Badge 
                                              className={`px-2 py-1 mr-2 sm:mr-0 ${
                                                item.status === 'active' 
                                                  ? 'bg-green-100 text-green-800 hover:bg-green-100' 
                                                  : item.status === 'expired' 
                                                  ? 'bg-red-100 text-red-800 hover:bg-red-100' 
                                                  : item.status === 'pending_renewal' 
                                                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' 
                                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
                                              }`}
                                            >
                                              {item.status === 'active' ? 'Active' : 
                                               item.status === 'expired' ? 'Expired' : 
                                               item.status === 'pending_renewal' ? 'Pending' : 
                                               item.status}
                                            </Badge>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                              onClick={() => handleUpdateReminder(item)}
                                            >
                                              Update Reminder
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                              onClick={() => setItemToDelete(item)}
                                            >
                                              Delete
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="needs-review">
          <PrescriptionDataEditor />
        </TabsContent>
      </Tabs>

      {/* Delete Item Dialog */}
      <AlertDialog 
        open={!!itemToDelete} 
        onOpenChange={() => setItemToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {itemToDelete?.product_name}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => itemToDelete && handleDeleteItem(itemToDelete)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Group Dialog */}
      <AlertDialog 
        open={!!groupToDelete} 
        onOpenChange={() => setGroupToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Items</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedItems.length} selected items?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Update Reminder Dialog */}
      <AlertDialog 
        open={!!reminderItem} 
        onOpenChange={(open) => !open && setReminderItem(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Reminder Date</AlertDialogTitle>
            <AlertDialogDescription>
              {reminderItem && (
                <div className="space-y-4 mt-4">
                  <div>
                    <p className="font-medium">Product: {reminderItem.product_name}</p>
                    
                    {/* Repeats and expiry info */}
                    <div className="flex flex-col mt-1">
                      {reminderItem.repeats !== undefined && (
                        <p className="text-sm text-blue-600">
                          Repeats remaining: {reminderItem.repeats}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">
                        Expiry date: {reminderItem.prescription_expiry_date ? 
                          new Date(reminderItem.prescription_expiry_date).toLocaleDateString() + ' (from prescription)' : 
                          new Date(reminderItem.expiry_date).toLocaleDateString() + ' (system default)'}
                      </p>
                    </div>
                    
                    <p className="text-sm text-gray-500 mt-2">
                      Current reminder date: {reminderItem.next_reminder_date 
                        ? new Date(reminderItem.next_reminder_date).toLocaleDateString() 
                        : 'None'}
                    </p>
                    {reminderItem.next_reminder_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        {(() => {
                          const now = new Date();
                          const reminderDate = new Date(reminderItem.next_reminder_date);
                          const daysDiff = Math.round((reminderDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                          
                          if (daysDiff < 0) {
                            return <span className="text-red-500">Overdue by {Math.abs(daysDiff)} days</span>;
                          } else if (daysDiff === 0) {
                            return <span className="text-orange-500">Scheduled for today</span>;
                          } else if (daysDiff === 1) {
                            return <span className="text-orange-500">Scheduled for tomorrow</span>;
                          } else {
                            return <span>Scheduled in {daysDiff} days</span>;
                          }
                        })()}
                      </p>
                    )}
                    {reminderItem.last_reminder_sent && (
                      <p className="text-xs text-gray-500 mt-1">
                        Last reminder sent: {new Date(reminderItem.last_reminder_sent).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">New Reminder Date</label>
                    <input
                      type="date"
                      value={newReminderDate}
                      onChange={(e) => setNewReminderDate(e.target.value)}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="clearLastReminder"
                      checked={clearLastReminder}
                      onChange={(e) => setClearLastReminder(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <label htmlFor="clearLastReminder" className="text-sm font-medium">
                      Clear last reminder (force new reminder to be sent)
                    </label>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={saveReminderUpdate}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Update Reminder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
} 